import { useRef, useState, type CSSProperties } from 'react';
import { extractCvDocument, UnsupportedFileError, DocxParseError, ScannedPdfError } from './lib/cvParse';
import { IconBack } from './components/icons';

// Evaluate/import P1 — INGEST. Two EQUAL-prominence paths get a user's existing CV into the app as plain
// text: (1) upload a .docx (client-side mammoth, see lib/cvParse), or (2) paste text (the universal fallback
// that covers PDF-havers). Both land in one `extracted` state, shown back for confirmation so the user can
// catch extraction garbage (two-column scramble, tables) BEFORE P2 segments it. P1 ends here: it calls
// onReady(text) with clean, user-confirmed CV text and creates NO entries, runs NO coverage. Firewall: this
// only ever holds text the user supplied (uploaded or pasted) — no system composition, no write path.

const MIN_CHARS = 40; // below this, an "extract" is almost certainly garbage / a wrong file — steer to paste.

export function ImportCv({ onReady, onCancel }: { onReady: (text: string, fileName?: string) => void; onCancel: () => void }) {
  const [paste, setPaste] = useState('');
  const [extracted, setExtracted] = useState(''); // '' = still on the input step; non-empty = confirm step
  /**
   * THE STRING THAT TRAVELS ONWARD, when it differs from the one on screen (R5.5, §11.3 / T-HIDE-1).
   *
   * A .docx or .pdf with hidden regions yields TWO strings: the visible text, which is what the confirm step
   * shows and counts, and the same text with `[[HIDDEN:…]]` sentinels, which is what the worker's
   * `sanitizeDocument()` needs in order to strip those regions, record them and raise `extraction_uncertain`.
   * Rendering the marked text would leak sentinel syntax into the preview; sending the previewed text would
   * throw the detector's finding away. So the preview is `extracted` and the payload is this. A paste has no
   * styling and no detector, so it leaves this null and the two strings are the same string.
   */
  const [payload, setPayload] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const pasteRef = useRef<HTMLTextAreaElement>(null);

  async function onFile(file: File | undefined) {
    if (!file) return;
    setError(''); setBusy(true);
    try {
      const doc = await extractCvDocument(file);
      if (doc.preview.length < MIN_CHARS) {
        setError("We couldn't read enough text from that file — try pasting your CV text instead.");
      } else {
        // THE FLOOR IS CHECKED ON THE VISIBLE TEXT, not the marked text. A document whose only bulk is hidden is
        // a document we could not usefully read, and counting sentinels toward the floor would hide that.
        setFileName(file.name); setExtracted(doc.preview); setPayload(doc.marked);
      }
    } catch (e) {
      if (e instanceof ScannedPdfError) {
        // The PDF opened fine but has no usable text layer (scanned/image-only) — the honest steer, mirroring
        // the docx-unreadable message: explain why, then give the always-works fallback.
        setError('This PDF looks scanned or image-only — we can’t read it reliably. Open it and paste the text instead.');
        pasteRef.current?.focus();
      } else if (e instanceof UnsupportedFileError && e.kind === 'pdf') {
        setError('We can’t read that PDF here. Open it, select all the text (Cmd/Ctrl-A), copy it, and paste it below.');
      } else if (e instanceof UnsupportedFileError) {
        setError('We can’t read that file type — upload a Word (.docx) or PDF file, or paste your CV text below.');
      } else if (e instanceof DocxParseError) {
        // The file IS a .docx but mammoth couldn't read it — explain + steer to paste (which always works).
        setError('We couldn’t read that Word file — it may be an unusual format (a .doc saved as .docx, or exported from Pages or Google Docs). Paste your CV text below instead.');
        pasteRef.current?.focus();
      } else {
        setError('Something went wrong reading that file — paste your CV text below instead.');
      }
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = ''; // allow re-selecting the same file after an error
    }
  }

  function usePaste() {
    const t = paste.trim();
    if (t.length < MIN_CHARS) { setError('That looks too short — paste the full text of your CV.'); return; }
    // A PASTE CLEARS THE PAYLOAD. Pasted text has no styling and no detector ran on it, so the marked and
    // visible strings are the same string; a stale payload from an earlier file upload must not survive here.
    setError(''); setFileName(''); setExtracted(t); setPayload(null);
  }

  function startOver() { setExtracted(''); setError(''); setPayload(null); }

  // ---- Confirmation checkpoint: show what we read before it feeds P2 ----
  if (extracted) {
    return (
      <main style={S.main} data-testid="import-cv">
        <button className="btn btn-ghost" style={S.back} onClick={onCancel} data-testid="import-cancel"><IconBack aria-hidden="true" /> Back</button>
        <h1>Here’s what we read from your CV</h1>
        <p style={S.help}>
          {fileName ? <>From <strong>{fileName}</strong>. </> : null}
          Check it looks right — columns and tables can scramble when copied. You’ll review everything again before
          it becomes part of your CV.
        </p>
        <pre style={S.preview} data-testid="import-preview">{extracted}</pre>
        <p style={S.count} data-testid="import-count">{extracted.length.toLocaleString()} characters read.</p>
        <div>
          <button className="btn btn-primary" onClick={() => onReady(payload ?? extracted, fileName || undefined)} data-testid="import-continue">Looks right — continue</button>
          <button className="btn btn-secondary" onClick={startOver} data-testid="import-restart">Start over</button>
        </div>
      </main>
    );
  }

  // ---- Input step: upload and paste, equal prominence ----
  return (
    <main style={S.main} data-testid="import-cv">
      <button className="btn btn-ghost" style={S.back} onClick={onCancel} data-testid="import-cancel"><IconBack aria-hidden="true" /> Back</button>
      <h1>Import your CV</h1>
      <p style={S.help}>Already have a CV? Bring it in — upload a Word document, or paste the text. Whichever’s easier.</p>

      <div style={S.cols}>
        <section style={S.col} data-testid="import-upload-panel">
          <h2 style={S.colH}>Upload a file</h2>
          <p style={S.colP}>A Word document (<strong>.docx</strong>), PDF, or plain text (.txt). We read it right here in your browser.</p>
          <input
            ref={fileRef}
            type="file"
            accept=".docx,.pdf,.txt,.md,text/plain,application/pdf"
            onChange={(e) => onFile(e.target.files?.[0])}
            disabled={busy}
            data-testid="import-file"
          />
          {busy && <p style={S.count} data-testid="import-busy">Reading…</p>}
        </section>

        <div style={S.or} aria-hidden="true">or</div>

        <section style={S.col} data-testid="import-paste-panel">
          <h2 style={S.colH}>Paste your CV</h2>
          <p style={S.colP}>Have a PDF? Open it, select all (Cmd/Ctrl-A), and paste here — this works for any format.</p>
          <textarea
            ref={pasteRef}
            style={S.ta}
            value={paste}
            onChange={(e) => setPaste(e.target.value)}
            placeholder="Paste the full text of your CV here…"
            data-testid="import-paste"
          />
          <button className="btn btn-primary" onClick={usePaste} disabled={!paste.trim()} data-testid="import-use-paste">Use this text</button>
        </section>
      </div>

      {error && <p style={S.err} data-testid="import-error">{error}</p>}
    </main>
  );
}

const S: Record<string, CSSProperties> = {
  // Import is a WORKING surface — wider than the landing's reading-width (scoped here only).
  main: { maxWidth: 960, margin: '40px auto', padding: '0 var(--space-4)', fontFamily: 'var(--sans)', lineHeight: 'var(--lh-body)' },
  back: { marginLeft: 0, paddingLeft: 0, fontSize: 'var(--fs-small)', marginBottom: 'var(--space-2)' },
  help: { color: 'var(--muted)', margin: 'var(--space-1) 0 var(--space-4)' },
  cols: { display: 'flex', gap: 'var(--space-4)', alignItems: 'stretch', flexWrap: 'wrap' },
  col: { flex: '1 1 280px', border: '1px solid var(--rule)', borderRadius: 10, padding: 'var(--space-4)', background: 'var(--card)' },
  colH: { fontSize: 'var(--fs-h3)', margin: '0 0 var(--space-2)' },
  colP: { fontSize: 'var(--fs-small)', color: 'var(--muted)', margin: '0 0 var(--space-3)' },
  or: { alignSelf: 'center', color: 'var(--muted)', fontSize: 'var(--fs-small)', fontStyle: 'italic' },
  // A CV is long: a generous, draggable, viewport-scaling paste target (not a cramped 7-row box).
  ta: { width: '100%', minHeight: '40vh', resize: 'vertical', padding: 'var(--space-2)', fontFamily: 'inherit', marginBottom: 'var(--space-1)' },
  // The correctability checkpoint — roomy review of the extraction before it feeds the segmenter.
  preview: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '60vh', overflow: 'auto', background: 'var(--surface-subtle)', border: '1px solid var(--rule)', borderRadius: 8, padding: 'var(--space-3)', fontSize: 'var(--fs-small)', fontFamily: 'inherit', margin: '0 0 var(--space-2)' },
  count: { fontSize: 'var(--fs-label)', color: 'var(--muted)', margin: 'var(--space-1) 0 var(--space-3)' },
  err: { color: 'var(--danger)', marginTop: 'var(--space-4)' },
};
